import React, { useMemo, useContext } from "react";

// internal-deps
import { PaymentMethod, PaymentMethodMeta, PaymentProgressLoader } from "@swiggy-private/payment-ui";
import { CheckoutContext, CheckoutActionTypes } from "@swiggy-private/checkout-core";
import { ApiFetchState, OnPayExtraParams } from "@swiggy-private/payment-core";
import { useMountedRef } from "@swiggy-private/core-ui";

// contexts
import { PaylaterLinkedContext } from "@payment-client/contexts/PaylaterLinkedContext";
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";

// helpers
import { findPayLaterPaymentMethod, sendLinkSuccessStatusToNative } from "@payment-client/helpers/PaylaterHelper";

// constants
import { PAYMENT_FALLBACK_TYPES } from "@payment-client/constants/PaymentFallback";

export interface AddPaylaterOrderProps {
    payAction: (paymentMethodData: PaymentMethod<PaymentMethodMeta>, _extraParams?: OnPayExtraParams) => void;
    payLaterPaymentCode?: string;
}

export function AddPaylaterOrderConfirm({
    payAction,
    payLaterPaymentCode,
}: AddPaylaterOrderProps): React.ReactElement | null {
    const { setCheckoutState } = useContext(CheckoutContext);
    const { paymentResponse } = React.useContext(ShowPaymentContext);
    const { paylaterState } = useContext(PaylaterLinkedContext);
    const isMountedRef = useMountedRef();
    const paymentCode = payLaterPaymentCode ?? PAYMENT_FALLBACK_TYPES.LAZYPAY;
    const paylaterMethod = useMemo(
        () => findPayLaterPaymentMethod(paymentResponse?.paymentGroup || [], paymentCode),
        [paymentCode, paymentResponse?.paymentGroup],
    );
    const payLaterLinkedData = paylaterState[paymentCode];

    React.useEffect(() => {
        const method = paylaterMethod.unwrap();
        const { fetchState, linked } = payLaterLinkedData;
        // if fetchstate is in loading state we will not do anything so that when state changes to success place order should happen
        if (fetchState === ApiFetchState.LOADING) {
            return;
        }

        if (
            !isMountedRef.current ||
            paylaterMethod.isNone() ||
            fetchState === ApiFetchState.ERROR ||
            !method.enabled ||
            !linked
        ) {
            setCheckoutState({ type: CheckoutActionTypes.SHOW_PAYMENTS });
            return;
        }

        if (fetchState === ApiFetchState.SUCCESS) {
            sendLinkSuccessStatusToNative(paymentCode, paymentResponse ? paymentResponse.tenant : undefined);
            setCheckoutState({ type: CheckoutActionTypes.SHOW_PAYMENTS });
            window.requestAnimationFrame(() => payAction(paylaterMethod.unwrap()));
            return;
        }
    }, [setCheckoutState, payAction, paylaterMethod, isMountedRef, payLaterLinkedData, paymentCode, paymentResponse]);

    if (paylaterMethod.isNone()) {
        return null;
    }

    return <PaymentProgressLoader />;
}
